/** Lógica pura do dashboard: formatação e intervalos. Nada de React aqui. */

export const MAX_RANGE_DAYS = 366;

// ---------- números ----------

const pct = new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 1 });
const int = new Intl.NumberFormat('pt-BR');

export const formatInt = (n: number) => int.format(n);

/** 0.1667 -> "16,7%". Entrada fora de 0..1 ou não numérica vira "0%". */
export function formatRate(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) return '0%';
  return pct.format(Math.min(1, Math.max(0, rate)));
}

/** Fatia de `value` no `total`, em 0..1. Total zero devolve 0 (nunca NaN). */
export function share(value: number, total: number): number {
  return total > 0 && value > 0 ? value / total : 0;
}

export const formatShare = (value: number, total: number) => formatRate(share(value, total));

export function plural(n: number, one: string, many: string): string {
  return `${formatInt(n)} ${n === 1 ? one : many}`;
}

// ---------- períodos ----------

export type Preset = 'month' | 'lastMonth' | 'last30';

export const PRESETS: { value: Preset; label: string }[] = [
  { value: 'month', label: 'Este mês' },
  { value: 'lastMonth', label: 'Mês passado' },
  { value: 'last30', label: 'Últimos 30 dias' },
];

const p2 = (n: number) => String(n).padStart(2, '0');
export const toIso = (d: Date) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;

export function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d, 12);
}

export function isValidIso(v: string | null | undefined): v is string {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  return toIso(parseIso(v)) === v;
}

export function presetRange(preset: Preset, now: Date = new Date()): { from: string; to: string } {
  const y = now.getFullYear();
  const m = now.getMonth();
  if (preset === 'month') return { from: toIso(new Date(y, m, 1, 12)), to: toIso(new Date(y, m + 1, 0, 12)) };
  if (preset === 'lastMonth') return { from: toIso(new Date(y, m - 1, 1, 12)), to: toIso(new Date(y, m, 0, 12)) };
  const end = new Date(y, m, now.getDate(), 12);
  const start = new Date(y, m, now.getDate() - 29, 12);
  return { from: toIso(start), to: toIso(end) };
}

/** Sem `from`/`to` na URL vale o mês corrente (igual ao padrão do servidor). */
export function resolveRange(from: string | null, to: string | null, now: Date = new Date()): { from: string; to: string } {
  const month = presetRange('month', now);
  const f = isValidIso(from) ? from : null;
  const t = isValidIso(to) ? to : null;
  if (f && t) return { from: f, to: t };
  if (f) return { from: f, to: toIso(new Date(parseIso(f).getTime() + 30 * 86_400_000)) };
  if (t) return { from: month.from <= t ? month.from : t, to: t };
  return month;
}

/** Qual preset corresponde ao intervalo (ou `null` se for personalizado). */
export function detectPreset(range: { from: string; to: string }, now: Date = new Date()): Preset | null {
  for (const p of PRESETS) {
    const r = presetRange(p.value, now);
    if (r.from === range.from && r.to === range.to) return p.value;
  }
  return null;
}

export function daysBetween(from: string, to: string): number {
  return Math.round((parseIso(to).getTime() - parseIso(from).getTime()) / 86_400_000);
}

/** Mensagem de erro (pt-BR) ou `null` se o período é aceitável. */
export function validateRange(from: string, to: string): string | null {
  if (!isValidIso(from) || !isValidIso(to)) return 'Informe datas válidas.';
  if (from > to) return 'A data final deve ser igual ou posterior à inicial.';
  if (daysBetween(from, to) > MAX_RANGE_DAYS) return `O período pode ter no máximo ${MAX_RANGE_DAYS} dias.`;
  return null;
}

export function formatPeriod(from: string, to: string): string {
  const f = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: from.slice(0, 4) === to.slice(0, 4) ? undefined : 'numeric' });
  const t = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  return from === to ? t.format(parseIso(to)) : `${f.format(parseIso(from))} a ${t.format(parseIso(to))}`;
}
